/**
 * 管理员管理（旧 views/system/AdminManagementView.vue）。
 * 所有管理员都可以查看列表和详情；只有超级管理员能添加、编辑、启用禁用、删除，非超管看不到操作列。
 * 超管不能在这里操作自己的账号。
 */
import { useEffect, useState } from 'react'
import { App, Button, Card, Col, Form, Input, Popconfirm, Row, Select, Space, Statistic, Table, Tag, Typography, type TableColumnsType } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { PageHeader } from '@/components/PageHeader'
import { useAuthStore } from '@/stores/auth'
import { AdminRole, type AdminListItem } from '@/types/admin'
import { ROLE_OPTIONS, formatAdminTime, roleColor, roleName } from './constants'
import {
  apiErrorMessage,
  useAdminList,
  useDeleteAdmin,
  useToggleAdminStatus,
  type AdminListFilters
} from './hooks'
import { AdminFormModal } from './AdminFormModal'
import { AdminDetailDrawer } from './AdminDetailDrawer'
import styles from './AdminManagementPage.module.css'

const ROLE_FILTER_OPTIONS = [{ value: '', label: '全部' }, ...ROLE_OPTIONS]
const STATUS_FILTER_OPTIONS = [
  { value: true, label: '活跃' },
  { value: false, label: '禁用' }
]

type FormState = { admin: AdminListItem | null } | null

export function AdminManagementPage() {
  const { message } = App.useApp()
  const isSuperAdmin = useAuthStore((state) => state.user?.role === AdminRole.SUPER_ADMIN)
  const currentAdminId = useAuthStore((state) => state.user?.id)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filters, setFilters] = useState<AdminListFilters>({ role: '', search: '' })
  const [searchText, setSearchText] = useState('')
  const [formState, setFormState] = useState<FormState>(null)
  const [detailId, setDetailId] = useState<number | null>(null)
  const { data, isFetching, error } = useAdminList(page, pageSize, filters)
  const toggleStatus = useToggleAdminStatus()
  const deleteAdmin = useDeleteAdmin()
  const admins = data?.admins ?? []

  useEffect(() => {
    if (error) message.error(apiErrorMessage(error, '加载管理员列表失败'))
  }, [error, message])

  // 改筛选条件时回到第一页
  const changeFilters = (patch: Partial<AdminListFilters>) => {
    setFilters((current) => ({ ...current, ...patch }))
    setPage(1)
  }

  const handleToggleStatus = async (admin: AdminListItem) => {
    try {
      await toggleStatus.mutateAsync(admin)
      message.success(`管理员已${admin.is_active ? '禁用' : '启用'}`)
    } catch (error_) {
      message.error(apiErrorMessage(error_, '状态更新失败'))
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteAdmin.mutateAsync(id)
      message.success('管理员删除成功')
    } catch (error_) {
      message.error(apiErrorMessage(error_, '删除失败'))
    }
  }

  const columns: TableColumnsType<AdminListItem> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      // 非超管没有操作列，点用户名查看详情
      render: (username: string, record) => (
        <Button type="link" size="small" className={styles.linkCell} onClick={() => setDetailId(record.id)}>
          {username}
        </Button>
      )
    },
    { title: '邮箱', dataIndex: 'email', key: 'email', width: 200 },
    { title: '显示名称', dataIndex: 'display_name', key: 'display_name', width: 120 },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => <Tag color={roleColor(role)}>{roleName(role)}</Tag>
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: boolean) => <Tag color={active ? 'success' : 'error'}>{active ? '活跃' : '禁用'}</Tag>
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 180,
      render: (value?: string) =>
        value ? formatAdminTime(value) : <Typography.Text type="secondary">从未登录</Typography.Text>
    }
  ]

  if (isSuperAdmin) {
    columns.push({
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => setDetailId(record.id)}>
            详情
          </Button>
          {record.id !== currentAdminId && (
            <>
              <Button type="link" size="small" onClick={() => setFormState({ admin: record })}>
                编辑
              </Button>
              <Popconfirm
                title={`确定要${record.is_active ? '禁用' : '启用'}该管理员吗？`}
                onConfirm={() => handleToggleStatus(record)}
              >
                <Button type="link" size="small" danger>
                  {record.is_active ? '禁用' : '启用'}
                </Button>
              </Popconfirm>
              <Popconfirm title="确定要删除该管理员吗？此操作不可恢复！" onConfirm={() => handleDelete(record.id)}>
                <Button type="link" size="small" danger>
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    })
  }

  return (
    <div>
      <PageHeader
        title="管理员管理"
        extra={
          isSuperAdmin && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormState({ admin: null })}>
              添加管理员
            </Button>
          )
        }
      />

      {/* 活跃、禁用数按当前页统计（与旧版一致） */}
      <Row gutter={16} className={styles.section}>
        <Col span={6}>
          <Card>
            <Statistic title="总管理员数" value={data?.total ?? 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="活跃管理员"
              value={admins.filter((admin) => admin.is_active).length}
              styles={{ content: { color: 'var(--ant-color-success)' } }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="禁用管理员"
              value={admins.filter((admin) => !admin.is_active).length}
              styles={{ content: { color: 'var(--ant-color-error)' } }}
            />
          </Card>
        </Col>
      </Row>

      <Card className={styles.section}>
        <Form layout="inline">
          <Form.Item label="角色">
            <Select
              aria-label="角色"
              className={styles.roleSelect}
              placeholder="全部角色"
              allowClear
              value={filters.role}
              options={ROLE_FILTER_OPTIONS}
              onChange={(value) => changeFilters({ role: value ?? '' })}
            />
          </Form.Item>
          <Form.Item label="状态">
            <Select
              aria-label="状态"
              className={styles.statusSelect}
              placeholder="全部状态"
              allowClear
              value={filters.is_active}
              options={STATUS_FILTER_OPTIONS}
              onChange={(value) => changeFilters({ is_active: value })}
            />
          </Form.Item>
          <Form.Item label="搜索">
            <Input.Search
              className={styles.searchInput}
              placeholder="搜索用户名/邮箱"
              allowClear
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onSearch={(value) => changeFilters({ search: value })}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table<AdminListItem>
          rowKey="id"
          columns={columns}
          dataSource={admins}
          loading={isFetching}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={(pagination) => {
            setPage(pagination.current ?? 1)
            setPageSize(pagination.pageSize ?? 20)
          }}
        />
      </Card>

      {isSuperAdmin && (
        <AdminFormModal
          open={formState !== null}
          admin={formState?.admin ?? null}
          onClose={() => setFormState(null)}
        />
      )}
      <AdminDetailDrawer adminId={detailId} onClose={() => setDetailId(null)} />
    </div>
  )
}
